// Pré-remplissage du titre d'un contenu à partir du document qu'on vient d'y attacher.
// Logique PURE, isolée du formulaire : elle n'a besoin d'aucun composant ni client API,
// ce qui la rend testable seule et réutilisable par tout type de contenu associant un
// champ titre et un champ document.

export type PickedDocumentInfo = {
	/** Titre saisi par l'éditeur au moment de l'envoi du média : la forme lisible. */
	title?: string | null;
	/** Nom du fichier stocké, repli quand le média n'a pas de titre. */
	fileName?: string | null;
};

/**
 * Libellé lisible déduit d'un document. Le titre du média prime, c'est une saisie humaine.
 * Le nom de fichier ne sert que de repli : les noms stockés étant désormais normalisés en
 * minuscules avec tirets bas, il faut lui retirer son extension et ses séparateurs.
 */
export const documentTitleFrom = (picked?: PickedDocumentInfo | null): string => {
	const assetTitle = (picked?.title ?? "").trim();
	if (assetTitle) return assetTitle;

	const raw = (picked?.fileName ?? "").trim();
	if (!raw) return "";
	const withoutExtension = raw.replace(/\.[A-Za-z0-9]{1,8}$/, "");
	const spaced = withoutExtension.replace(/[_\-]+/g, " ").replace(/\s{2,}/g, " ").trim();
	if (!spaced) return "";
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Renvoie les valeurs du formulaire avec le champ titre rempli SI et seulement si celui-ci
 * est vide. Un titre déjà saisi n'est jamais écrasé : la saisie de l'éditeur prime toujours.
 * `titleKey` vaut null quand le type de contenu n'a pas de champ titre, et rien ne change.
 */
export const withDocumentAutoTitle = (
	next: Record<string, any>,
	titleKey: string | null | undefined,
	picked?: PickedDocumentInfo | null
): Record<string, any> => {
	if (!picked || !titleKey) return next;

	const current = next[titleKey];
	const alreadyFilled = typeof current === "string" ? current.trim().length > 0 : current != null && current !== "";
	if (alreadyFilled) return next;

	const derived = documentTitleFrom(picked);
	return derived ? { ...next, [titleKey]: derived } : next;
};
